import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { type SyntheticEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ApiProblem,
  type AggregateCoverage,
  type ApplicationPage,
  type ApplicationQuery,
  type ApplicationSummary,
} from '../api/client'
import { useSession } from '../auth/SessionProvider'
import './ApplicationList.css'

/** The columns a person can sort by, and the contract's name for each. */
const SORTABLE = { name: 'Application', vendor: 'Vendor', machine_count: 'Installations' } as const
type SortKey = keyof typeof SORTABLE

function queryOf(params: URLSearchParams): ApplicationQuery {
  const query: Record<string, string> = {}
  for (const name of [
    'search',
    'collection',
    'freshness',
    'category',
    'sort',
    'direction',
    'cursor',
  ]) {
    const value = params.get(name)
    if (value) {
      query[name] = value
    }
  }
  return query
}

/**
 * One page of applications for the view in the URL. Shared by the table and
 * the tab count, so both read one request.
 */
export function useApplicationPage() {
  const { client, state } = useSession()
  const [params] = useSearchParams()
  const query = queryOf(params)
  const generation = state.status === 'signedIn' ? state.generation : undefined
  return useQuery({
    queryKey: ['applications', generation, query],
    enabled: generation !== undefined,
    queryFn: ({ signal }) => client.applications(query, signal),
    // A sort, search or page change keeps the current rows on screen while the
    // next page loads, instead of blanking the table to a loading message.
    placeholderData: keepPreviousData,
  })
}

export function ApplicationList() {
  const { data, error, isPending, isPlaceholderData } = useApplicationPage()
  const [params] = useSearchParams()

  if (isPending) {
    return (
      <p className="notice" role="status" aria-live="polite">
        Loading applications…
      </p>
    )
  }
  if (error) {
    return <Failure error={error} />
  }
  return (
    <section aria-labelledby="applications-summary">
      <Search current={params.get('search') ?? ''} />
      <Result page={data} search={params.get('search') ?? ''} busy={isPlaceholderData} />
    </section>
  )
}

function Result({ page, search, busy }: { page: ApplicationPage; search: string; busy: boolean }) {
  const { result, filtered_count: filtered, total_count: total } = page.page

  switch (result) {
    case 'empty':
      return (
        <p className="notice" id="applications-summary">
          No applications have been reported. Collection succeeded and found nothing to list.
        </p>
      )
    case 'filtered_empty':
      return (
        <p className="notice" id="applications-summary">
          No applications match “{search}”. <Link to="/inventory/apps">Clear the search</Link>
        </p>
      )
    case 'unknown':
      // Empty's counts, and a different fact: the read could not account for
      // the whole fleet. Saying "no applications" here would state an absence
      // the backend deliberately refused to state.
      return (
        <p className="notice is-warn" id="applications-summary">
          The inventory cannot say what is installed yet: not every endpoint has reported. This is
          not a report that nothing is installed.
        </p>
      )
    case 'rows':
      return (
        <>
          <Incomplete coverage={page.coverage} />
          <h3 className="summary" id="applications-summary">
            Showing {page.items.length} of {filtered} applications
            {filtered < total ? <span className="summary-of"> ({total} in total)</span> : null}
          </h3>
          <Table items={page.items} busy={busy} />
          <Pages next={page.page.next_cursor} />
        </>
      )
  }
}

/**
 * Said when some endpoints have not fully reported. The rows shown are real;
 * the list may simply not be all of them.
 */
function Incomplete({ coverage }: { coverage: AggregateCoverage }) {
  const unfinished =
    (coverage.by_collection['partial'] ?? 0) +
    (coverage.by_collection['failed'] ?? 0) +
    (coverage.by_collection['pending'] ?? 0) +
    coverage.never_succeeded_count
  const stale = coverage.by_freshness['stale'] ?? 0
  if (unfinished === 0 && stale === 0 && coverage.omitted_source_count === 0) {
    return null
  }
  return (
    <p className="notice is-warn" role="note">
      This list may be incomplete: some endpoints have not fully reported, or their last report is
      out of date.
    </p>
  )
}

function Search({ current }: { current: string }) {
  const [params, setParams] = useSearchParams()
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()
    const value = new FormData(event.currentTarget).get('search')
    const next = new URLSearchParams(params)
    if (typeof value === 'string' && value.trim() !== '') {
      next.set('search', value.trim())
    } else {
      next.delete('search')
    }
    // A new search starts at the first page: a cursor belongs to the old one.
    next.delete('cursor')
    setParams(next)
  }
  return (
    <form className="search" role="search" onSubmit={submit}>
      <label className="sr-only" htmlFor="applications-search">
        Search applications
      </label>
      <input
        id="applications-search"
        name="search"
        type="search"
        placeholder="Search applications"
        defaultValue={current}
        key={current}
      />
    </form>
  )
}

function Table({ items, busy }: { items: readonly ApplicationSummary[]; busy: boolean }) {
  return (
    <div className="table-wrap">
      <table className="apps" aria-busy={busy}>
        <caption className="sr-only">Applications installed across the fleet</caption>
        <thead>
          <tr>
            <SortHeader column="name" />
            <SortHeader column="vendor" />
            <SortHeader column="machine_count" />
            <th scope="col">Category</th>
          </tr>
        </thead>
        <tbody>
          {items.map((app) => (
            <tr key={app.application_id}>
              <th scope="row">
                <span className="app-mark" aria-hidden="true">
                  {initials(app.name)}
                </span>
                <span className="app-name">{app.name}</span>
              </th>
              <td>{app.vendor ?? <span className="absent">— unknown vendor</span>}</td>
              <td className="number">{app.machine_count}</td>
              <td>
                {app.category === null ? (
                  // Not a category, so not drawn as one. Null folds together
                  // three different situations, none of which is a category.
                  <span className="absent">— no category</span>
                ) : (
                  <span className="pill" data-testid="category">
                    {app.category}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SortHeader({ column }: { column: SortKey }) {
  const [params, setParams] = useSearchParams()
  const active = params.get('sort') === column
  const direction = active && params.get('direction') === 'desc' ? 'desc' : 'asc'
  const next = active && direction === 'asc' ? 'desc' : 'asc'

  const sort = () => {
    const updated = new URLSearchParams(params)
    updated.set('sort', column)
    updated.set('direction', next)
    // The order changed, so any cursor points into a different sequence.
    updated.delete('cursor')
    setParams(updated)
  }

  return (
    <th
      scope="col"
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="sort" onClick={sort}>
        {SORTABLE[column]}
        <span aria-hidden="true">{active ? (direction === 'asc' ? ' ▲' : ' ▼') : ''}</span>
      </button>
    </th>
  )
}

function Pages({ next }: { next: string | null }) {
  const [params] = useSearchParams()
  const onLaterPage = params.has('cursor')
  if (next === null && !onLaterPage) {
    return null
  }
  const nextParams = new URLSearchParams(params)
  if (next !== null) {
    nextParams.set('cursor', next)
  }
  const firstParams = new URLSearchParams(params)
  firstParams.delete('cursor')

  return (
    <nav className="pages" aria-label="Pages">
      {onLaterPage ? <Link to={`?${firstParams.toString()}`}>First page</Link> : null}
      {next !== null ? <Link to={`?${nextParams.toString()}`}>Next page</Link> : null}
    </nav>
  )
}

/** Each failure says what happened and never implies an empty inventory. */
function Failure({ error }: { error: Error }) {
  const problem = error instanceof ApiProblem ? error : undefined

  if (problem?.code === 'cursor_rejected') {
    return (
      <p className="notice">
        That page is no longer available. <Link to="/inventory/apps">Go to the first page</Link>
      </p>
    )
  }
  if (problem?.status === 401) {
    return (
      <p className="notice" role="alert">
        Your session has ended. <a href="/api/auth/login">Sign in again</a>
      </p>
    )
  }
  if (problem?.status === 429) {
    return (
      <p className="notice is-warn" role="alert">
        Too many requests in a short time. Wait a moment, then reload the page.
      </p>
    )
  }
  if (problem?.status === 403) {
    return (
      <p className="notice is-bad" role="alert">
        Your account no longer has access to the console. Ask an administrator for access.
      </p>
    )
  }
  return (
    <p className="notice is-bad" role="alert">
      The findings service could not be reached, so this page cannot say what is installed. This is
      not a report that nothing was found.
      {problem?.traceId ? <span className="trace"> Reference: {problem.traceId}</span> : null}
    </p>
  )
}

/** Up to two letters for the mark beside a name. Decorative, and hidden. */
function initials(name: string): string {
  const words = name.split(/[\s\-_.]+/).filter((word) => /^[\p{L}\p{N}]/u.test(word))
  const first = words[0]?.[0] ?? name.slice(0, 1)
  const second = words.length > 1 ? (words[1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}
